import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import {
  useAthleteVolumes,
  useDailyStats,
  useDateSelector,
  useDatesAttachments,
  usePhaseSelection,
  usePhaseSummaryData,
  useWeekSelection,
} from "../../../../hooks/annual-planner";
import {
  getDefaultPhases,
  resolveMilestones,
} from "../../../../utils/annual-planner-helpers";
import { whoIsAthleteCoach } from "../../../../utils/user-helpers";
import {
  CalendarView,
  Header,
  ListView,
  PhaseSummary,
  WeekNotesPanel,
  WeekPlanPreview,
  WeekStepper,
  WindowStepper,
} from "./parts";

// Stable empty fallback so a missing/absent tracking config never hands
// WeekPlanPreview a fresh object reference on every render.
const EMPTY_TRACKING_ITEMS = {};

export const AnnualPlanner = ({ locals }) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const {
    query,
    activeUser,
    phasesTemplates,
    athleteStats,
    athleteVolumes,
    athlete,
    clientGoals = [],
    coachOrganizationInformation,
    clientConfiguration,
  } = locals;

  // Already loaded unconditionally for every Profile tab (drives the tab
  // strip's "pending" badge - see profile.controller.js), so reading it here
  // costs nothing extra. WeekPlanPreview's tracking card reads the exact same
  // sections.tracking.items shape calendar-month.controller.js's Plan view
  // does - no fetch of its own needed, and no reason to refetch it per week
  // like the plan/log data itself (it isn't date-ranged).
  const trackingItems =
    clientConfiguration?.sections?.tracking?.items || EMPTY_TRACKING_ITEMS;

  // `athlete.annualPlan` is already normalized server-side (ensure-coach.js
  // loads it through msb-api's switch-aware periodization endpoint, which
  // always answers in this exact legacy shape) - `athletePhases` is exactly
  // what this component and its children (Header/CalendarView/ListView)
  // already expect, no local transform needed.
  const athletePhases = athlete?.annualPlan?.trainingPhases || {};
  // Same legacy shape as athletePhases, on the nutrition track - populated
  // once an athlete actually has dated nutrition phases (writes are no
  // longer blocked; see currentNutritionPhase's own comment on why every
  // athlete still reads empty today regardless).
  const {
    datesAttachments: nutritionDatesAttachments = [],
    phases: nutritionPhases = [],
  } = athlete?.annualPlan?.nutritionPhases || {};

  // All 5 PhaseSummary status-chip values, memoized on their real
  // dependencies inside the hook - see usePhaseSummaryData's own comment.
  const {
    trainingPhase,
    nutritionPhase,
    bodyweightGoal,
    dailyTargets,
    customGoals,
    nextMilestone,
  } = usePhaseSummaryData({
    athlete,
    athleteStats,
    activeUser,
    clientGoals,
    t,
  });

  const startingCalendarDay =
    coachOrganizationInformation?.startingCalendarDay ||
    activeUser.startingCalendarDay ||
    0;

  const organizationCoach = query.organizationCoach || undefined;
  const organizationCoachId = organizationCoach
    ? whoIsAthleteCoach(
        activeUser.athletes,
        query.athlete,
        activeUser._id,
        organizationCoach
      )
    : undefined;

  const defaultPhases = getDefaultPhases(phasesTemplates);

  const { from, athleteSwitch, changedTemplate, statsFrom, prevFrom } = query;
  const {
    datesAttachments = [],
    phases: storedPhases = [],
    // Lifted to state (below) so the Weekly Notes panel can update it
    // in-place after a no-reload save/delete, without a full page reload -
    // Calendar view and List view both read the same state, so they stay
    // in sync too.
    phasesDescriptions: phasesDescriptionsInitial = [],
    hasStoredPhases,
  } = athletePhases;
  const [phasesDescriptions, setPhasesDescriptions] = useState(
    phasesDescriptionsInitial
  );
  // Mirrors the original `phases = defaultPhases` destructuring default,
  // which only fired when the athlete had genuinely never had a
  // `trainingPhases.phases` key at all - not when it existed but was empty
  // (e.g. their last custom phase was just removed). The read model collapses
  // both cases to an empty array, so `hasStoredPhases` is what the old
  // destructuring default used to infer from key presence/absence.
  const phases = hasStoredPhases ? storedPhases : defaultPhases;

  const { units } = activeUser;

  const [reloading, setReloading] = useState(false);
  // Nutrition's own reloading flag, independent of training's - the two
  // tracks' quick-assign grids submit independently (see
  // usePhaseSelection's `track` param), so one track mid-save must not
  // block clicks on the other.
  const [nutritionReloading, setNutritionReloading] = useState(false);
  const { athleteStatsState } = useDailyStats({
    dailyStats: athleteStats,
    prevFrom,
  });
  const { athleteVolumesState } = useAthleteVolumes({
    prevFrom,
    athleteVolumes,
  });
  const { phasesAttachments, setPhasesAttachments } = useDatesAttachments({
    athleteSwitch,
    changedTemplate,
    datesAttachments,
    setReloading,
  });
  const {
    phasesAttachments: nutritionPhasesAttachments,
    setPhasesAttachments: setNutritionPhasesAttachments,
  } = useDatesAttachments({
    athleteSwitch,
    changedTemplate,
    datesAttachments: nutritionDatesAttachments,
    setReloading: setNutritionReloading,
  });
  const { dateFromState, goToPreviousMonth, goToNextMonth, resetWindow } =
    useDateSelector({
      from,
      startingCalendarDay,
      statsFrom,
    });

  const {
    selectedWeek,
    selectWeek,
    focusSignal,
    goToPreviousWeek,
    goToNextWeek,
    goToThisWeek: selectThisWeek,
    hasPreviousWeek,
    hasNextWeek,
  } = useWeekSelection({ dateFromState, startingCalendarDay });

  // "This Week" also brings the 12-month window back to the current period
  const goToThisWeek = () => {
    resetWindow();
    selectThisWeek();
  };

  const events = athlete?.annualPlan?.events || [];
  const windowStart = moment(dateFromState).startOf("month");
  const windowEnd = moment(windowStart).add(12, "months");
  const isInWindow = (date) =>
    date.isSameOrAfter(windowStart) && date.isBefore(windowEnd);
  const calendarEvents = events.filter((event) =>
    isInWindow(moment(event.fromDate, "YYYYMMDD"))
  );
  // Same 12-month window cutoff as calendarEvents above, applied to the
  // merged events+dated-goals list (see resolveMilestones) for the grid's
  // Milestone row - matches what Calendar view actually renders, rather
  // than every milestone the athlete has ever had.
  const calendarMilestones = resolveMilestones(clientGoals, events).filter(
    (milestone) => isInWindow(moment(String(milestone.utcDate), "YYYYMMDD"))
  );

  // Mirror scrollbar (see useMirrorScrollbar.js, already used by Nutrition
  // Progress's TableView.js and this same page's own ListView.js). Rendered
  // as .planner-wrapper's sibling below, both wrapped together in a plain
  // div with no overflow of its own - that wrapper is what bounds where the
  // sticky strip stops sticking (right after the calendar, not through
  // WeekStepper/WeekNotesPanel below). #annual-planner used to have
  // its own `overflow: auto` (removed, see annual-planner.css) which made
  // IT the sticky positioning context instead of the real viewport - with
  // that in place the strip never stuck at all, regardless of DOM position.
  const [mirrorScrollbar, setMirrorScrollbar] = useState(null);
  const [listView, setListView] = useState(false);
  const [selectedDaysState, setSelectedDaysState] = useState([]);
  const annualPlannerMainRef = useRef();

  const { handlePhaseSelection } = usePhaseSelection(
    phasesAttachments,
    setPhasesAttachments,
    athlete,
    reloading,
    setReloading,
    organizationCoachId,
    "training"
  );
  const { handlePhaseSelection: handleNutritionPhaseSelection } =
    usePhaseSelection(
      nutritionPhasesAttachments,
      setNutritionPhasesAttachments,
      athlete,
      nutritionReloading,
      setNutritionReloading,
      organizationCoachId,
      "nutrition"
    );

  // Tracked Results is scoped to these 4 signals per PRD 7.6 - kCal/Steps
  // dropped (see StatsCard.js's own comment).
  const athleteStatsHeaders = [
    t("athlete-portal:annualPlanner.statsReadiness"),
    t("athlete-portal:annualPlanner.statsSessionRpe"),
    units === "metric"
      ? t("athlete-portal:annualPlanner.statsWeeklyVolumeKg")
      : t("athlete-portal:annualPlanner.statsWeeklyVolumeLbs"),
    units === "metric"
      ? t("athlete-portal:annualPlanner.bodyWeightKg")
      : t("athlete-portal:annualPlanner.bodyWeightLbs"),
  ];

  useEffect(() => {
    // We just refresh the page to clean the params if the cleaning failed in the
    // profile.controller.js - See that file for better understanding
    if (
      window &&
      String(window.performance.getEntriesByType("navigation")[0].type) ===
        "reload" &&
      prevFrom
    ) {
      submit("/actions/refresh", {
        method: "GET",
        body: {
          statsFrom: "",
          prevFrom: "",
          changedTemplate: "",
        },
      });
    }
  }, []);

  return (
    <article id="annual-planner" ref={annualPlannerMainRef}>
      <PhaseSummary
        trainingPhase={trainingPhase}
        nutritionPhase={nutritionPhase}
        bodyweightGoal={bodyweightGoal}
        nextMilestone={nextMilestone}
        dailyTargets={dailyTargets}
        customGoals={customGoals}
      />

      <Header
        listView={listView}
        setListView={setListView}
        selectedDaysState={selectedDaysState}
        setSelectedDaysState={setSelectedDaysState}
        phasesTemplates={phasesTemplates}
        athlete={athlete}
        athleteId={athlete._id}
        organizationCoachId={organizationCoachId}
      />

      <WindowStepper
        dateFromState={dateFromState}
        onPreviousMonth={goToPreviousMonth}
        onNextMonth={goToNextMonth}
      />

      {!listView ? (
        <div className="calendar-scroll-boundary">
          <section className="planner-wrapper">
            <CalendarView
              phases={phases}
              nutritionPhases={nutritionPhases}
              nutritionPhasesAttachments={nutritionPhasesAttachments}
              handleNutritionPhaseSelection={handleNutritionPhaseSelection}
              calendarEvents={calendarEvents}
              milestones={calendarMilestones}
              events={events}
              units={units}
              athleteStatsHeaders={athleteStatsHeaders}
              dateFromState={dateFromState}
              startingCalendarDay={startingCalendarDay}
              selectedDaysState={selectedDaysState}
              setSelectedDaysState={setSelectedDaysState}
              phasesAttachments={phasesAttachments}
              setPhasesAttachments={setPhasesAttachments}
              handlePhaseSelection={handlePhaseSelection}
              phasesDescriptions={phasesDescriptions}
              setMirrorScrollbar={setMirrorScrollbar}
              athleteStatsState={athleteStatsState}
              athleteVolumesState={athleteVolumesState}
              selectedWeek={selectedWeek}
              onSelectWeek={selectWeek}
            />
          </section>
          {mirrorScrollbar?.needsScroll && (
            <div
              className="planner-mirror-scroll"
              ref={mirrorScrollbar.mirrorRef}
              onScroll={mirrorScrollbar.onScrollMirror}
            >
              <div
                className="planner-mirror-scroll__spacer"
                style={{ width: mirrorScrollbar.scrollWidth }}
              />
            </div>
          )}
        </div>
      ) : (
        <ListView
          dateFromState={dateFromState}
          startingCalendarDay={startingCalendarDay}
          phases={phases}
          datesAttachments={datesAttachments}
          nutritionPhases={nutritionPhases}
          nutritionDatesAttachments={nutritionDatesAttachments}
          phasesDescriptions={phasesDescriptions}
          clientGoals={clientGoals}
          events={events}
          athleteStatsState={athleteStatsState}
          athleteVolumesState={athleteVolumesState}
          units={units}
          onSelectWeek={selectWeek}
        />
      )}

      <WeekStepper
        selectedWeek={selectedWeek}
        onPreviousWeek={goToPreviousWeek}
        onNextWeek={goToNextWeek}
        onThisWeek={goToThisWeek}
        hasPreviousWeek={hasPreviousWeek}
        hasNextWeek={hasNextWeek}
      />
      <WeekNotesPanel
        selectedWeek={selectedWeek}
        datesAttachments={datesAttachments}
        phases={phases}
        nutritionDatesAttachments={nutritionDatesAttachments}
        nutritionPhases={nutritionPhases}
        clientGoals={clientGoals}
        events={events}
        phasesDescriptions={phasesDescriptions}
        setPhasesDescriptions={setPhasesDescriptions}
        athleteId={athlete._id}
        organizationCoachId={organizationCoachId}
        focusSignal={focusSignal}
      />
      <WeekPlanPreview
        selectedWeek={selectedWeek}
        athlete={athlete}
        athleteId={athlete._id}
        organizationCoach={organizationCoach}
        organizationCoachId={organizationCoachId}
        units={units}
        trackingItems={trackingItems}
      />
    </article>
  );
};
